import { useEffect, useState, useRef, useCallback } from 'react'
import LocalizedLink from '@/components/layout/LocalizedLink'
import Card from '@/components/ui/Card'
import Button from '@/components/ui/Button'
import { libraryApi } from '@/services/libraryApiService'
import { getLocalizedValue } from '@sakeena/shared'
import { useTranslation } from 'react-i18next'
import { useCurrentLanguage } from '@/utils/contentVisibility'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { cn } from '@/utils/cn'

// Helper to normalize book data
const normalizeBook = (book: any): any => ({
  id: book.id || book.Id || '',
  title: book.title || book.Title || book.name || book.Name || '',
  author: book.author || book.Author || book.authorName || book.writer || '',
  coverImageUrl: book.coverImageUrl || book.CoverImageUrl || book.imageUrl || book.ImageUrl || book.thumbnailUrl || '',
  isActive: book.isActive ?? book.IsActive ?? true,
})

const CARD_GAP = 24
const CARD_MIN_WIDTH = 300
const CARD_MAX_WIDTH = 360

export default function FeaturedBooks() {
  const { t } = useTranslation()
  const safeT = t || ((key: string, def: string) => def || key)
  const currentLang = useCurrentLanguage()
  const [books, setBooks] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const scrollRef = useRef<HTMLDivElement>(null)
  const isDraggingRef = useRef(false)
  const startXRef = useRef(0)
  const startScrollLeftRef = useRef(0)

  useEffect(() => {
    const fetchBooks = async () => {
      try {
        setLoading(true)
        setError(null)

        const res = await libraryApi.getBooks()
        const raw = Array.isArray(res) ? res : []

        const normalized = raw.map(normalizeBook)
        const active = normalized.filter((b) => b.isActive !== false)
        setBooks(active)
      } catch (err) {
        console.error('Failed to load books:', err)
        setError(safeT('home.featuredBooks.loadingError', 'فشل تحميل الكتب المميزة'))
      } finally {
        setLoading(false)
      }
    }

    fetchBooks()
  }, [safeT, currentLang])

  const scrollBy = useCallback((delta: number) => {
    const el = scrollRef.current
    if (!el) return
    const isRTL = document.documentElement.dir === 'rtl'
    const sign = isRTL ? -1 : 1
    el.scrollBy({ left: sign * delta, behavior: 'smooth' })
  }, [])

  const scrollPrev = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    const cardWidth = CARD_MIN_WIDTH + CARD_GAP
    scrollBy(-cardWidth)
  }, [scrollBy])

  const scrollNext = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    const cardWidth = CARD_MIN_WIDTH + CARD_GAP
    scrollBy(cardWidth)
  }, [scrollBy])

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (!scrollRef.current) return
    if ((e.target as HTMLElement).closest('a, button')) return
    isDraggingRef.current = true
    startXRef.current = e.pageX - scrollRef.current.offsetLeft
    startScrollLeftRef.current = scrollRef.current.scrollLeft
  }, [])

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isDraggingRef.current || !scrollRef.current) return
    e.preventDefault()
    const x = e.pageX - scrollRef.current.offsetLeft
    const walk = x - startXRef.current
    scrollRef.current.scrollLeft = startScrollLeftRef.current - walk
  }, [])

  const handleMouseUpOrLeave = useCallback(() => {
    isDraggingRef.current = false
  }, [])

  if (loading) {
    return (
      <section className="py-12 md:py-16 bg-gray-50 dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-xl md:text-2xl font-semibold text-slate-700 mb-4">
              {safeT('home.featuredBooks.subtitle', 'مجموعة مختارة من أفضل الكتب الإسلامية')}
            </h2>
            <p className="text-slate-600 text-sm">{safeT('common.loading', 'جاري تحميل الكتب المميزة...')}</p>
          </div>
          <div className="flex justify-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-600" />
          </div>
        </div>
      </section>
    )
  }

  if (error) {
    return (
      <section className="py-12 md:py-16 bg-gray-50 dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-xl md:text-2xl font-semibold text-slate-700 mb-2">
              {safeT('home.featuredBooks.subtitle', 'مجموعة مختارة من أفضل الكتب الإسلامية')}
            </h2>
            <p className="text-slate-600 text-sm">{error}</p>
          </div>
        </div>
      </section>
    )
  }

  if (books.length === 0) return null

  return (
    <section className="py-12 md:py-16 bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <h2 className="text-xl md:text-2xl font-semibold text-slate-800 dark:text-slate-100">
            {safeT('home.featuredBooks.subtitle', 'مجموعة مختارة من أفضل الكتب الإسلامية')}
          </h2>
        </div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <button
          type="button"
          onClick={scrollPrev}
          aria-label={safeT('common.previous', 'السابق')}
          className={cn(
            'absolute top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-white/90 hover:bg-gray-100 border border-gray-200',
            'flex items-center justify-center text-slate-600 hover:text-slate-900 transition-colors shadow-[0_2px_8px_rgba(0,0,0,0.06)]',
            'left-0'
          )}
        >
          <ChevronLeft className="w-5 h-5" />
        </button>

        <button
          type="button"
          onClick={scrollNext}
          aria-label={safeT('common.next', 'التالي')}
          className={cn(
            'absolute top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-white/90 hover:bg-gray-100 border border-gray-200',
            'flex items-center justify-center text-slate-600 hover:text-slate-900 transition-colors shadow-[0_2px_8px_rgba(0,0,0,0.06)]',
            'right-0'
          )}
        >
          <ChevronRight className="w-5 h-5" />
        </button>

        <div
          ref={scrollRef}
          role="region"
          aria-label={safeT('home.featuredBooks.title', 'الكتب المميزة')}
          className={cn(
            'flex gap-6 overflow-x-auto overflow-y-hidden scroll-smooth snap-x snap-mandatory py-4 pb-6',
            'select-none [scrollbar-width:none] [-webkit-overflow-scrolling:touch]',
            'cursor-grab active:cursor-grabbing [&::-webkit-scrollbar]:hidden'
          )}
          style={{ scrollSnapType: 'x mandatory' }}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUpOrLeave}
          onMouseLeave={handleMouseUpOrLeave}
        >
          {books.map((book) => {
            const title = book.title || safeT('home.featuredBooks.untitledBook', 'كتاب بدون عنوان')
            const author = book.author || safeT('home.featuredBooks.unknownAuthor', 'مؤلف غير معروف')
            const coverUrl = book.coverImageUrl

            return (
              <Card
                key={String(book.id)}
                className={cn(
                  'shrink-0 snap-start rounded-xl border-0 bg-white dark:bg-gray-800',
                  'flex flex-col min-w-[300px] max-w-[360px]',
                  'shadow-[0_4px_20px_rgba(0,0,0,0.08),0_0_1px_rgba(0,0,0,0.06)]'
                )}
                style={{
                  minWidth: CARD_MIN_WIDTH,
                  maxWidth: CARD_MAX_WIDTH,
                  scrollSnapAlign: 'start',
                }}
              >
                {/* صف المحتوى: عنوان + مؤلف على اليسار، غلاف صغير مربع على اليمين (مثل Figma) */}
                <div className="flex flex-1 flex-row gap-4 p-5 min-h-0">
                  <div className="flex-1 min-w-0 order-1 rtl:order-2 flex flex-col justify-start">
                    <h3 className="text-base md:text-lg font-bold text-slate-900 dark:text-slate-100 leading-snug line-clamp-3 break-words">
                      {getLocalizedValue(title, currentLang)}
                    </h3>
                    <p className="text-slate-600 dark:text-slate-400 text-sm mt-1.5 line-clamp-2 break-words">
                      {getLocalizedValue(author, currentLang)}
                    </p>
                  </div>
                  <div className="shrink-0 order-2 rtl:order-1 w-20 h-20 md:w-24 md:h-24 rounded-lg overflow-hidden bg-slate-100 dark:bg-slate-700 shadow-[0_2px_8px_rgba(0,0,0,0.08)]">
                    {coverUrl ? (
                      <img
                        src={coverUrl}
                        alt={getLocalizedValue(title, currentLang)}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.currentTarget.src = 'https://via.placeholder.com/200x300?text=Book'
                        }}
                      />
                    ) : (
                      <div className="w-full h-full bg-slate-200 dark:bg-slate-600" />
                    )}
                  </div>
                </div>
                {/* زر في الأسفل في المنتصف مثل Figma */}
                <div className="px-5 pb-5 pt-2 flex justify-center">
                  <LocalizedLink to={`/library/${book.id}`}>
                    <Button
                      variant="primary"
                      className="rounded-xl px-6 py-2.5 text-sm font-medium bg-emerald-600 hover:bg-emerald-700 text-white border-0"
                    >
                      {safeT('home.featuredBooks.readBook', 'قراءة الكتاب')}
                    </Button>
                  </LocalizedLink>
                </div>
              </Card>
            )
          })}
        </div>
      </div>
    </section>
  )
}
